import { useEffect } from "react";
import { useLatest } from "../../hooks/useLatest";

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
};

type NetworkInformation = {
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
};

type SocketLike = {
  connected: boolean;
  emit: (event: string, payload?: unknown) => unknown;
};

type Options = {
  socket: SocketLike;
  connect: () => void;
  disconnect: () => void;
  // The space/channel the user is looking at. Read at call time, so they are refs.
  currentVoidIdRef: { current: string };
  currentChannelIdRef: { current: string };
  // Tells the native (Android) wrapper which channel is on screen so it can clear that channel's notifications.
  notifyChannelViewed: (voidId: string, channelId: string) => void;
};

const IDLE_MS = 10 * 60 * 1000;
const FOCUS_HEARTBEAT_MS = 30000;

/**
 * Keeps the server's picture of this device accurate, and keeps the live connection alive:
 *  - tells the server when the app is focused/visible and which channel is open (so push
 *    notifications route to the right device), now and every 30 s;
 *  - disconnects while the tab is hidden (so a backgrounded tab doesn't show as online) and
 *    reconnects when it is visible again;
 *  - reconnects right away when the network returns or the connection type changes;
 *  - marks the user idle after 10 minutes without input and active again on the next input.
 *
 * Moved out of ChatLayout with the same behaviour (one small improvement: the one-time first report is
 * now cancelled if the component unmounts before it fires). It owns no state and renders nothing.
 */
export function useConnectionPresence({
  socket,
  connect,
  disconnect,
  currentVoidIdRef,
  currentChannelIdRef,
  notifyChannelViewed,
}: Options) {
  // The heartbeat effect below runs once; reach the newest callback through a ref.
  const notifyChannelViewedRef = useLatest(notifyChannelViewed);

  // Focus/visibility heartbeat to improve server push routing (presence-aware)
  useEffect(() => {
    const emitFocus = () => {
      try {
        const focused = (document.visibilityState === 'visible') && document.hasFocus();
        const vid = currentVoidIdRef.current;
        const cid = currentChannelIdRef.current ? `${vid}:${currentChannelIdRef.current}` : '';
        socket.emit('client:focus', { focused, voidId: vid, channelId: cid });
        if (focused && vid && currentChannelIdRef.current) {
          notifyChannelViewedRef.current(vid, currentChannelIdRef.current);
        }
      } catch { /* noop */ }
    };
    const onFocus = () => emitFocus();
    const onBlur = () => emitFocus();
    const onVis = () => emitFocus();
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVis);
    const t = setInterval(emitFocus, FOCUS_HEARTBEAT_MS);
    // Initial emit
    const initial = setTimeout(emitFocus, 0);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(t);
      clearTimeout(initial);
    };
  }, [socket, currentVoidIdRef, currentChannelIdRef, notifyChannelViewedRef]);

  // Disconnect sockets when hidden to avoid showing online/mobile presence while backgrounded.
  useEffect(() => {
    const onVis = () => {
      try {
        if (document.visibilityState === 'hidden') {
          disconnect();
        } else {
          connect();
        }
      } catch { /* noop */ }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [connect, disconnect]);

  // Reconnect sockets immediately when network comes back
  useEffect(() => {
    const onOnline = () => { try { if (!socket.connected) connect(); } catch { /* noop */ } };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [connect, socket]);

  // Hint presence changes based on tab visibility
  useEffect(() => {
    const onVis = () => {
      try { socket.emit('presence:visibility', { state: document.visibilityState }); } catch { /* noop */ }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [socket]);

  // Idle detection — 10 minutes of no input marks the user idle; any activity clears it.
  useEffect(() => {
    let idleTimer: number | null = null;
    let currentlyIdle = false;

    function markIdle() {
      if (currentlyIdle) return;
      currentlyIdle = true;
      try { socket.emit('presence:idle'); } catch { /* noop */ }
    }

    function markActive() {
      if (idleTimer !== null) { clearTimeout(idleTimer); idleTimer = null; }
      if (currentlyIdle) {
        currentlyIdle = false;
        try { socket.emit('presence:active'); } catch { /* noop */ }
      }
      idleTimer = window.setTimeout(markIdle, IDLE_MS);
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;
    events.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));

    // Start the timer immediately
    idleTimer = window.setTimeout(markIdle, IDLE_MS);

    return () => {
      if (idleTimer !== null) clearTimeout(idleTimer);
      events.forEach((ev) => window.removeEventListener(ev, markActive));
    };
  }, [socket]);

  // React quickly to connection type changes (e.g., Wi‑Fi ↔ LTE)
  useEffect(() => {
    const nav = navigator as NavigatorWithConnection;
    const conn = nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
    if (!conn || !conn.addEventListener) return;
    const onChange = () => { try { if (!socket.connected) connect(); } catch { /* noop */ } };
    conn.addEventListener('change', onChange);
    return () => { try { conn.removeEventListener?.('change', onChange); } catch { /* noop */ } };
  }, [connect, socket]);
}

